import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Store } from '@ngxs/store';
import { InspectionTireDto } from 'remarketing-angular-library/';
import { InspectionImageDto, EditPostingState, UpdateAllTireInspections, UpdateTireInspection } from 'remarketing-angular-library';

export interface TireFormData {
  location: string;
  manufacturer: string;
  size: string;
  tread: string;
  comments?: string;
  damageCost?: string;
  tireImages?: InspectionImageDto[];
  expanded?: boolean;
  markTheSame?: boolean;
}

interface DrawerState {
  [location: string]: boolean;
}

@Injectable()
export class TiresFormService implements DataSource<TireFormData> {
  static formDataToDto(data: TireFormData): InspectionTireDto {
    const { expanded, markTheSame, ...formData } = data;
    return {
      ...formData,
      damageCost: Number(formData.damageCost)
    };
  }

  private readonly drawerState = new BehaviorSubject<DrawerState>({});
  // Unsaved form state
  private transientState: { [location: string]: TireFormData } = {};

  constructor(private readonly store: Store) {}

  updateTransientState(formData: TireFormData) {
    this.transientState[formData.location] = formData;
  }

  getTransientState(location: string) {
    return this.transientState[location];
  }

  clearTransientState(location: string) {
    delete this.transientState[location];
  }

  updateTire(data: TireFormData) {
    this.store.dispatch(new UpdateTireInspection(TiresFormService.formDataToDto(data)));
  }

  updateAllTires(data: TireFormData) {
    this.store.dispatch(new UpdateAllTireInspections(TiresFormService.formDataToDto(data)));
  }

  connect(): Observable<TireFormData[]> {
    return combineLatest([
      this.store
        .select(EditPostingState.updatedPosting)
        .pipe(map(s => s.inventoryItem?.inspectionReport?.inspectionData?.vehicle?.tires || [])),
      this.drawerState.asObservable()
    ]).pipe(
      map(([tires, drawerState]) => {
        return ['FL', 'FR', 'RL', 'RR', 'SP'].map(location => {
          const tireForLocation = this.findTire(location, tires);
          return this.toFormData(tireForLocation, drawerState);
        });
      })
    );
  }

  toggleDrawer(location: string) {
    const state = this.drawerState.getValue();
    const locationState = state[location];
    this.drawerState.next({
      ...this.drawerState.getValue(),
      [location]: !locationState
    });
  }

  disconnect(collectionViewer: CollectionViewer): void {
    // noop
  }

  private toFormData(apiTire: InspectionTireDto, drawerState: DrawerState): TireFormData {
    return {
      ...apiTire,
      damageCost: apiTire.damageCost ? apiTire.damageCost.toString() : '',
      expanded: Boolean(drawerState[apiTire.location])
    };
  }

  private findTire(location: string, tires: InspectionTireDto[]): InspectionTireDto {
    const tire = tires.find(t => t.location === location);
    return (
      tire || {
        location,
        manufacturer: '',
        size: '',
        tread: '',
        comments: '',
        damageCost: undefined
      }
    );
  }

  closeDrawer(location) {
    this.drawerState.next({
      ...this.drawerState.getValue(),
      [location]: false
    });
  }
}
